import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, PhoneCall, MessageSquare, User } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Brand";
import WorkspaceFilter from "@/components/WorkspaceFilter";
import { api, errMsg, useData } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStickyState } from "@/lib/useStickyState";
import { STATUSES, STATUS_LABELS, fullName, TEMP_STYLE, TEMPERATURES } from "@/lib/crm";

const COL_ACCENT = { New: "#3B185F", Contacted: "#0284C7", Scheduled: "#D4AF37", Closed: "#059669" };

function PipelineMetrics({ contacts }) {
  const total = contacts.length;
  const signed = contacts.filter((c) => c.status === "Closed").length;
  const conv = total ? Math.round((signed / total) * 100) : 0;
  return (
    <div className="mb-6 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="pipeline-metrics">
      {STATUSES.map((s) => (
        <div key={s} className="rounded-xl border bg-white p-3" data-testid={`metric-${s}`}>
          <div className="font-display text-2xl font-extrabold text-[#19082B]">{contacts.filter((c) => c.status === s).length}</div>
          <div className="flex items-center gap-1.5 text-xs text-stone-500"><span className="h-2 w-2 rounded-full" style={{ background: COL_ACCENT[s] }} />{STATUS_LABELS[s]}</div>
        </div>
      ))}
      <div className="rounded-xl bg-[#19082B] p-3 text-white" data-testid="metric-conversion">
        <div className="font-display text-2xl font-extrabold text-[#D4AF37]">{conv}%</div>
        <div className="text-xs text-white/70">New Inquiry → Signed Up ({signed}/{total})</div>
      </div>
    </div>
  );
}

export default function Pipeline() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const navigate = useNavigate();
  const [contacts, reload] = useData("/contacts", []);
  const [associates] = useData(isAdmin ? "/admin/students" : null, []);
  const [workspace, setWorkspace] = useStickyState("pipeline.workspace", "all");
  const [tempFilter, setTempFilter] = useStickyState("pipeline.tempFilter", "all");
  const [labelFilter, setLabelFilter] = useStickyState("pipeline.labelFilter", "all");
  const [over, setOver] = useState(null);
  const allLabels = [...new Set(contacts.flatMap((c) => c.tags || []))].sort();
  const base = workspace === "all" ? contacts : contacts.filter((c) => c.owner_id === workspace || c.assigned_to === workspace);
  const tempScoped = tempFilter === "all" ? base : base.filter((c) => c.temperature === tempFilter);
  const scoped = labelFilter === "all" ? tempScoped : tempScoped.filter((c) => (c.tags || []).includes(labelFilter));

  const remove = async (c) => {
    if (!window.confirm(`Delete ${fullName(c)}? This removes the lead from Contacts and Pipeline.`)) return;
    try { await api.delete(`/contacts/${c.id}`); toast.success(`${fullName(c)} deleted`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const move = async (id, status) => {
    const c = contacts.find((x) => x.id === id);
    if (!c || c.status === status) return;
    try { await api.patch(`/contacts/${id}/status`, { status }); toast.success(`${fullName(c)} moved to ${STATUS_LABELS[status]}`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Pipeline" subtitle="Drag a lead card between stages, or use its menu to move it.">
        <Select value={tempFilter} onValueChange={setTempFilter}>
          <SelectTrigger className="w-40" data-testid="pipeline-temp-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All temperatures</SelectItem>
            {TEMPERATURES.map((t) => <SelectItem key={t} value={t} data-testid={`pipeline-temp-filter-${t}`}>{t} leads</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={labelFilter} onValueChange={setLabelFilter}>
          <SelectTrigger className="w-40" data-testid="pipeline-label-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All labels</SelectItem>
            {allLabels.map((l) => <SelectItem key={l} value={l} data-testid={`pipeline-label-filter-${l}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <WorkspaceFilter associates={associates} value={workspace} onChange={setWorkspace} />
      </PageHeader>
      <PipelineMetrics contacts={scoped} />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="pipeline-board">
        {STATUSES.map((s) => {
          const cards = scoped.filter((c) => c.status === s);
          return (
            <div key={s}
              onDragOver={(e) => { e.preventDefault(); setOver(s); }}
              onDragLeave={() => setOver(null)}
              onDrop={(e) => { setOver(null); move(e.dataTransfer.getData("id"), s); }}
              className={`rounded-2xl border bg-white/70 p-3 min-h-[460px] transition-colors ${over === s ? "drop-active" : ""}`}
              style={{ borderTop: `4px solid ${COL_ACCENT[s]}` }}
              data-testid={`pipeline-column-${s}`}>
              <div className="flex items-center justify-between px-1 py-1.5 mb-2">
                <div className="flex items-center gap-2 font-display font-bold text-sm text-[#19082B]">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: COL_ACCENT[s] }} />{STATUS_LABELS[s]}
                </div>
                <span className="text-xs font-mono text-stone-500" data-testid={`pipeline-count-${s}`}>{cards.length}</span>
              </div>
              <div className="space-y-2">
                {cards.map((c) => (
                  <div key={c.id} draggable onDragStart={(e) => e.dataTransfer.setData("id", c.id)}
                    className="group rounded-xl border bg-white p-3 shadow-sm cursor-grab active:cursor-grabbing transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md"
                    style={{ borderLeft: `3px solid ${COL_ACCENT[s]}` }} data-testid={`pipeline-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm text-[#19082B] flex items-center gap-1.5">{fullName(c)}{c.temperature && <span className={`rounded-full border px-1.5 text-[9px] font-bold ${TEMP_STYLE[c.temperature]}`} data-testid={`pipeline-temp-${c.id}`}>{c.temperature}</span>}</div>
                      <button type="button" onClick={() => remove(c)} className="p-1 -mr-1 text-stone-300 hover:text-red-600 transition-colors" title="Delete lead" data-testid={`pipeline-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="text-xs text-stone-500 truncate">{c.email || c.phone || "—"}</div>
                    {c.assigned_to_name && <div className="mt-1 flex items-center gap-1 text-[11px] text-[#3B185F]"><User className="h-3 w-3" />{c.assigned_to_name}</div>}
                    <div className="mt-2 flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => navigate("/dialer", { state: { contactId: c.id } })} className="rounded-md border p-1 text-[#3B185F] hover:bg-[#F6F2FB]" title="Dial" data-testid={`pipeline-dial-${c.id}`}><PhoneCall className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => navigate("/sms", { state: { contactId: c.id } })} className="rounded-md border p-1 text-[#3B185F] hover:bg-[#F6F2FB]" title="SMS" data-testid={`pipeline-sms-${c.id}`}><MessageSquare className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="mt-2 flex flex-col gap-1">
                      {s === "New" && <button type="button" onClick={() => move(c.id, "Contacted")} className="rounded-md bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-100" data-testid={`pipeline-action-contacted-${c.id}`}>Mark as Contacted</button>}
                      {(s === "New" || s === "Contacted") && <button type="button" onClick={() => move(c.id, "Scheduled")} className="rounded-md bg-[#FFFDF0] px-2 py-1 text-xs font-semibold text-[#8a6a10] hover:bg-[#FBF3D6]" data-testid={`pipeline-action-schedule-${c.id}`}>Schedule Session</button>}
                      {s !== "Closed" && <button type="button" onClick={() => move(c.id, "Closed")} className="rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100" data-testid={`pipeline-action-close-${c.id}`}>Close - Tier 1</button>}
                    </div>
                    <Select value={s} onValueChange={(v) => move(c.id, v)}>
                      <SelectTrigger className="mt-2 h-7 text-xs" data-testid={`pipeline-move-${c.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((x) => <SelectItem key={x} value={x} data-testid={`pipeline-move-option-${x}`}>Move to {STATUS_LABELS[x]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
